import { createSlice, createAsyncThunk, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { fetchPatientRecord } from "@common/lib/supabaseClient";
import { loadPatientRecordPreferPrefetched } from "../patientRecordLoad";
import { setAllDocuments, computeTabs, setActivePatientRecordId } from "./activeDocumentsSlice";
import {
    PatientRecord,
    TranscriptSection,
    PatientRecordFetchErrors,
    ActivePatientStatus,
    ActivePatientData,
    MultiPatientData,
    TranscriptSectionStatus,
    DocumentAsset,
} from "@common/utils/types";
import * as Sentry from "@sentry/react";
import { RootState } from "@/app/store";
import { determineErrorStateFile, determineErrorState } from "@/common/utils/helpers";

const initialState: ActivePatientData = {
    patientVisits: [],
    patientData: null,
    transcriptSections: [],
    documentAssets: [],
    multiplePatients: [],
    status: ActivePatientStatus.Idle,
    error: null,
    selectedAssetIds: [],
    userHasDeselectedAsset: false,
    discardedSectionIds: [],
};

const activePatientDataSlice = createSlice({
    name: "activePatientData",
    initialState,
    reducers: {
        setPatientVisits(state, action) {
            state.patientVisits = action.payload;
        },
        setPatientData(state, action) {
            state.patientData = action.payload;
            // Patient switch boundary — drop tombstones from the prior patient. Clearing lives
            // here (not in `setTranscriptSections`) because `setTranscriptSections` is also
            // dispatched on SAME-patient refetches (`handleActivePatientBroadcast`, Clip
            // display-name save); clearing there opens a race where a delayed non-terminal
            // broadcast (Uploaded/InProgress) can resurrect a just-discarded section.
            // `setPatientData` is only dispatched from `fetchPatientData` on actual switch.
            state.discardedSectionIds = [];
        },
        setTranscriptSections(state, action) {
            state.transcriptSections = action.payload;
            // Do NOT clear `discardedSectionIds` here — see `setPatientData` for why.
        },
        setDocumentAssets(state, action) {
            state.documentAssets = action.payload;
        },
        updateDocumentAsset(state, action: PayloadAction<{ id: string; updates: Partial<DocumentAsset> }>) {
            const { id, updates } = action.payload;
            const index = state.documentAssets.findIndex((asset) => asset.id === id);
            if (index !== -1) {
                state.documentAssets[index] = { ...state.documentAssets[index], ...updates };
            }
        },
        setErrorState(state, action) {
            state.status = ActivePatientStatus.Failed;
            state.error = action.payload;
        },
        setMultiplePatients(state, action) {
            state.multiplePatients = action.payload;
        },
        setSelectedMultiPatient: (state, action: PayloadAction<MultiPatientData>) => {
            state.multiplePatients[action.payload.index].isSelected = !action.payload.isSelected;
        },
        updatePatientData: (state, action: PayloadAction<PatientRecord>) => {
            // determine if the patient record is a history record
            const isHistory = state.patientVisits.find((visit) => visit.id === action.payload.id)?.isHistory || false;

            // Only recompute multi-pet checkboxes when the payload actually carries the name.
            // Partial-payload callers (e.g. StageSelect dispatches `{ id, stage }`) would
            // otherwise wipe valid checkbox state to []. The name-edit path (PatientInfo)
            // always sends `patient_name`, so stopword/single-pet transitions still clear
            // stale state correctly — EN-626.
            const historyNameProvided = isHistory && action.payload.patients?.name !== undefined;
            const nameInPayload = historyNameProvided || "patient_name" in action.payload;
            // Inside this branch we ALWAYS reassign — null/empty/falsy names resolve to "" and
            // clear multiplePatients via checkForMultiplePatientNames(""). The only preserve-
            // state path is `nameInPayload === false` (e.g. stage-only dispatches).
            if (nameInPayload) {
                const newName =
                    isHistory && action.payload.patients?.name
                        ? action.payload.patients?.name
                        : action.payload.patient_name || "";
                state.multiplePatients = checkForMultiplePatientNames(newName);
            }

            state.patientData = { ...state.patientData, ...action.payload };

            // Step 2. Update the patient visits in the state
            state.patientVisits = state.patientVisits.map((visit) =>
                visit.id === action.payload.id
                    ? {
                          ...visit,
                          scheduledAt: action.payload.scheduled_at || null,
                          appointmentDuration: action.payload.appointment_duration ?? visit.appointmentDuration,
                      }
                    : visit,
            );
        },
        /**
         * @deprecated Name is misleading — this is update-only despite "OrAdd". Renaming has
         * wider blast radius (multiple call sites + tests); deferred. For new explicit-add
         * call sites use `addTranscriptSection`. Update-only callers can keep using this.
         */
        updateOrAddTranscriptSection: (
            state,
            action: PayloadAction<{ transcriptId: string; transcriptSection: TranscriptSection }>,
        ) => {
            // Update-only. Despite the legacy name, this reducer does NOT push new sections —
            // the push-branch was a resurrection vector: a stale realtime event arriving
            // between `removeTranscriptSections` and the next ref sync would land here with
            // findIndex=-1 and re-add the discarded section. Brand-new sections go through
            // `addTranscriptSection` (explicit-add) or `fetchPatientData`.
            if (!state.patientData) return;
            if (state?.patientData?.transcriptions?.id !== action.payload.transcriptId) return;
            const index = state.transcriptSections.findIndex(
                (section) => section.id === action.payload.transcriptSection?.id,
            );
            if (index !== -1) {
                state.transcriptSections[index] = {
                    ...state.transcriptSections[index],
                    ...action.payload.transcriptSection,
                };
            }
        },
        addTranscriptSection: (
            state,
            action: PayloadAction<{ transcriptId: string; transcriptSection: TranscriptSection }>,
        ) => {
            // Idempotent explicit-add. Used by upload flows (useFileUpload) that need to seed
            // a brand-new section into Redux before the next refetch. If the section is already
            // present, merge-updates it (safe to dup-call). `created_at` fallback preserves
            // sort stability in Assets.tsx.
            if (!state.patientData) return;
            if (state?.patientData?.transcriptions?.id !== action.payload.transcriptId) return;
            // Tombstone guard: refuse to re-add a section the user just discarded. The cross-client
            // new-section path in RealtimeProvider also checks tombstones (so it can emit a
            // breadcrumb), but enforce here too so any future caller is protected at the reducer
            // boundary. See transcriptSectionResurrection.spec.ts.
            if (state.discardedSectionIds.includes(action.payload.transcriptSection?.id)) {
                return;
            }
            const index = state.transcriptSections.findIndex(
                (section) => section.id === action.payload.transcriptSection?.id,
            );
            if (index !== -1) {
                state.transcriptSections[index] = {
                    ...state.transcriptSections[index],
                    ...action.payload.transcriptSection,
                };
            } else {
                state.transcriptSections.push({
                    ...action.payload.transcriptSection,
                    created_at: action.payload.transcriptSection.created_at || new Date().toISOString(),
                });
            }
        },
        removeTranscriptSections: (state, action: PayloadAction<string[]>) => {
            // Removes transcript sections whose ids are in the action.payload array.
            state.transcriptSections = state.transcriptSections.filter(
                (section) => !action.payload.includes(section.id),
            );
            // Tombstone the removed ids so RealtimeProvider's cross-client new-section path
            // won't resurrect them from a delayed non-terminal broadcast that was in flight
            // at discard time (the Uploaded/InProgress broadcast typically races the archive
            // broadcast). All local-discard callers route through this reducer, so tombstoning
            // here keeps the 3 call sites unchanged.
            //
            // Bounded growth: cleared on every patient switch via `setPatientData`. A single
            // visit's tombstone list is capped by how many clips a vet can realistically
            // discard in one session (low double digits at worst); no FIFO/TTL cap needed.
            for (const id of action.payload) {
                if (!state.discardedSectionIds.includes(id)) {
                    state.discardedSectionIds.push(id);
                }
            }
        },
        resetActivePatientData: () => initialState,
        toggleAssetSelection: (state, action: PayloadAction<string>) => {
            const index = state.selectedAssetIds.indexOf(action.payload);
            if (index === -1) {
                state.selectedAssetIds.push(action.payload);
            } else {
                state.selectedAssetIds.splice(index, 1);
                state.userHasDeselectedAsset = true;
            }
        },
        setAssetAsSelected: (state, action: PayloadAction<string>) => {
            // if the asset is not already selected, add it to the selected asset ids
            if (!state.selectedAssetIds.includes(action.payload)) {
                state.selectedAssetIds.push(action.payload);
            }

            // otherwise, the asset is already selected, so we do nothing
        },
        setAssetsSelection: (state, action: PayloadAction<string[] | { ids: string[]; curated: boolean }>) => {
            if (Array.isArray(action.payload)) {
                state.selectedAssetIds = action.payload;
                state.userHasDeselectedAsset = false;
            } else {
                state.selectedAssetIds = action.payload.ids;
                state.userHasDeselectedAsset = action.payload.curated;
            }
        },
        selectAllAssets: (state) => {
            const allAssetIds = [
                // filter out clips that have an error state
                ...state.transcriptSections
                    .filter(
                        (section) =>
                            determineErrorState(section, state.patientData?.chat_completions || []) !== "error",
                    )
                    .map((section) => section.id),
                ...state.documentAssets
                    .filter(
                        (asset) =>
                            determineErrorStateFile(asset, state.patientData?.chat_completions || []) !== "error",
                    )
                    .map((asset) => asset.id),
            ];
            state.selectedAssetIds = allAssetIds;
            state.userHasDeselectedAsset = false;
        },
        unselectAllAssets: (state) => {
            state.selectedAssetIds = [];
            state.userHasDeselectedAsset = true;
        },
        handleVisitUpdated: (state, action: PayloadAction<{ visitId: string; updatedAt: string }>) => {
            const { visitId, updatedAt } = action.payload;

            // if the updatedAt is not provided or is null, return
            if (!updatedAt) return;

            state.patientVisits = state.patientVisits.map((visit) => {
                // if the visit id is not the same as the visitId, return the visit
                if (visit.id !== visitId) return visit;

                // if the visit does not have an updatedAt, set it to the updatedAt
                if (!visit.updatedAt) {
                    return { ...visit, updatedAt: updatedAt };
                }

                // if the updatedAt is more recent than the current updatedAt, update the updatedAt
                if (updatedAt > visit.updatedAt) {
                    return { ...visit, updatedAt: updatedAt };
                }

                return visit;
            });
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPatientData.pending, (state) => {
                state.status = ActivePatientStatus.Loading;
            })
            .addCase(fetchPatientData.fulfilled, (state, _action) => {
                state.status = ActivePatientStatus.Succeeded;
            })
            .addCase(fetchPatientData.rejected, (state) => {
                state.status = ActivePatientStatus.Failed;
                state.error = PatientRecordFetchErrors.FETCH_FAILED;
            });
    },
});

//Actions
export const {
    setTranscriptSections,
    setDocumentAssets,
    setErrorState,
    setPatientVisits,
    setPatientData,
    setMultiplePatients,
    updatePatientData,
    setSelectedMultiPatient,
    resetActivePatientData,
    updateOrAddTranscriptSection,
    addTranscriptSection,
    removeTranscriptSections,
    updateDocumentAsset,
    toggleAssetSelection,
    setAssetAsSelected,
    setAssetsSelection,
    selectAllAssets,
    unselectAllAssets,
    handleVisitUpdated,
} = activePatientDataSlice.actions;

//Selectors
export const getPatientData = (state: RootState) => state.activePatientData.patientData;
export const getPatientVisits = (state: RootState) => state.activePatientData.patientVisits;
export const getPatientRecordFetchStatus = (state: RootState) => state.activePatientData.status;
export const getPatientRecordFetchError = (state: RootState) => state.activePatientData.error;
export const getPatientStage = (state: RootState) => state.activePatientData.patientData?.stage;
export const getPatientRecordId = (state: RootState) => state.activePatientData.patientData?.id;
export const getPatientId = (state: RootState) => state.activePatientData.patientData?.patients?.id;
export const getTranscriptSections = (state: RootState) => state.activePatientData.transcriptSections;
export const getDiscardedSectionIds = (state: RootState) => state.activePatientData.discardedSectionIds;
export const getDocumentAssets = (state: RootState) => state.activePatientData.documentAssets;
export const getMultiplePatients = (state: RootState) => state.activePatientData.multiplePatients;
export const getSelectedAssetIds = (state: RootState) => state.activePatientData.selectedAssetIds;
export const getUserHasDeselectedAsset = (state: RootState) => state.activePatientData.userHasDeselectedAsset;
export const getPatientContact = (state: RootState) => state.activePatientData.patientData?.contacts;

export const isPatientRecordActive = createSelector(
    [getPatientData, (state: RootState, patientRecordId: string) => patientRecordId],
    (patientData, patientRecordId) => patientData?.id === patientRecordId,
);

export const isProcessingTranscriptSections = createSelector([getTranscriptSections], (transcriptSections) => {
    return transcriptSections.some(
        (section) =>
            section.status === TranscriptSectionStatus.InProgress ||
            section.status === TranscriptSectionStatus.Uploaded ||
            section.status === TranscriptSectionStatus.NotStarted,
    );
});

export const isAssetSelected = createSelector(
    [getSelectedAssetIds, (state: RootState, assetId: string) => assetId],
    (selectedAssetIds, assetId) => selectedAssetIds.includes(assetId),
);

export const getSelectedAssets = createSelector(
    [getSelectedAssetIds, getTranscriptSections, getDocumentAssets],
    (selectedAssetIds, transcriptSections, documentAssets) => {
        const selectedTranscriptSections = transcriptSections.filter((section) =>
            selectedAssetIds.includes(section.id),
        );
        const selectedDocumentAssets = documentAssets.filter((asset) => selectedAssetIds.includes(asset.id));
        return [...selectedTranscriptSections, ...selectedDocumentAssets];
    },
);

//Async Thunks
export const fetchPatientData = createAsyncThunk(
    "activePatientData/fetchPatientData",
    async (patientRecordId: string, { dispatch }) => {
        try {
            const { data, error, chatCompletions, patientVisits } =
                await loadPatientRecordPreferPrefetched(patientRecordId);
            if (!data && error === PatientRecordFetchErrors.NO_PATIENT_RECORD) {
                dispatch(setErrorState(PatientRecordFetchErrors.NO_PATIENT_RECORD));
            } else if (!data && error === PatientRecordFetchErrors.FETCH_FAILED) {
                dispatch(setErrorState(PatientRecordFetchErrors.FETCH_FAILED));
            } else {
                let isHistory: boolean | undefined = undefined;
                if (patientVisits) {
                    dispatch(setPatientVisits(patientVisits));
                    isHistory = patientVisits.find((visit) => visit.id === data?.id)?.isHistory || false;
                }

                // Set state
                dispatch(setAllDocuments(chatCompletions || []));
                dispatch(computeTabs());
                dispatch(setTranscriptSections(data?.transcriptions?.transcript_sections || []));
                dispatch(setDocumentAssets(data?.document_assets));

                // default asset selection to all transcript sections and document assets
                dispatch(
                    setAssetsSelection([
                        ...(data?.transcriptions?.transcript_sections?.map((section) => section.id) || []),
                        ...(data?.document_assets?.map((asset) => asset.id) || []),
                    ]),
                );

                // Select the correct patient name based on if it is a history record or not.
                // Dispatch unconditionally — parser is source of truth, including its []
                // verdict (EN-626 stopword/single-pet cases must clear stale state).
                const multiplePatientNames = checkForMultiplePatientNames(
                    isHistory && data?.patients?.name ? data?.patients?.name : data?.patient_name || "",
                );
                dispatch(setMultiplePatients(multiplePatientNames));
                dispatch(setPatientData(data));

                // set active patient record id in activeDocumentsSlice
                dispatch(setActivePatientRecordId(data?.id || ""));
            }
        } catch {
            Sentry.captureException("Patient record fetch failed.");
            dispatch(setErrorState(PatientRecordFetchErrors.FETCH_FAILED));
        }
    },
);

// This thunk is used to handle the patient event broadcasted from realtime
export const handleActivePatientBroadcast = createAsyncThunk(
    "activePatientData/handleActivePatientBroadcast",
    async ({ patientRecordId }: { patientRecordId: string }, { dispatch, getState }) => {
        const state = getState() as RootState;
        const { patientData } = state.activePatientData;

        // if there is no patient data or the patient data is not the same as the patient record id, return null
        if (!patientData || patientData.id !== patientRecordId) {
            return null;
        }

        // get the record from the db
        const { data: patientRecord, error } = await fetchPatientRecord(patientRecordId);

        // if there is an error or no patient record, return null
        if (error || !patientRecord) {
            return null;
        }

        // PR review finding: the isActivePatient check above only guards entry into this thunk --
        // nothing re-verified it after the await. fetchPatientRecord is a real network round-trip;
        // the user can navigate to a DIFFERENT visit while it's in flight (activeDocumentsSlice's
        // setActivePatientRecordId/patientData.id both change on that navigation, independent of
        // this fetch). Without re-checking, the stale response below would merge the PREVIOUS
        // patient's record into the newly-opened visit via updatePatientData (unconditional
        // {...state.patientData, ...action.payload} merge) and replace its transcriptSections
        // wholesale -- corrupting the visit the user actually navigated to.
        const { patientData: currentPatientData, discardedSectionIds } = (getState() as RootState).activePatientData;
        if (currentPatientData?.id !== patientRecordId) {
            return null;
        }

        // update the patient data in the state
        dispatch(updatePatientData(patientRecord));

        // PR review finding: setTranscriptSections wholesale-replaces transcriptSections with
        // whatever this fetch returned, with no tombstone check (unlike addTranscriptSection,
        // which already guards against discardedSectionIds -- see that reducer's own comment).
        // A section can still be in the fetch response despite being locally discarded: the
        // caller's own dispatch(removeTranscriptSections(...)) (which tombstones it) runs
        // synchronously, but the paired server-side deleteTranscriptSection/archive call is
        // async and can still be in flight when this fetch lands. discardedSectionIds above was
        // read fresh alongside the still-active-patient check (not the `state` captured before
        // the await), since more discards can happen while this fetch was in flight.
        const transcriptSections = (patientRecord?.transcriptions?.transcript_sections || []).filter(
            (section) => !discardedSectionIds.includes(section.id),
        );

        // update the transcript sections in the state
        dispatch(setTranscriptSections(transcriptSections));

        return patientRecord;
    },
);

// Mirror of API-Server `getMultiplePatientsArray` (app/domain/utils.ts).
// EN-626: keep these in sync until the server's parsed list ships in the patient_record
// response (deferred follow-up — see specs/2026-05-14-ios-explicit-patient-name-deferred.md).
const MULTI_PATIENT_STOPWORDS = new Set([
    "family",
    "household",
    "friends",
    "recheck",
    "rechecks",
    "followup",
    "follow-up",
    "mr",
    "mrs",
    "ms",
    "dr",
    "mr.",
    "mrs.",
    "ms.",
    "dr.",
]);

// Constructed via `new RegExp` so the project's pre-ES6 tsconfig target accepts the `u` flag.
const LEADING_LETTER = new RegExp("^\\p{L}", "u");
const TRAILING_PUNCT = /[.,;:!?]+$/;

const isLikelyPatientName = (name: string): boolean => {
    if (!name || name.length < 2) return false;
    if (!LEADING_LETTER.test(name)) return false;
    if (MULTI_PATIENT_STOPWORDS.has(name.toLowerCase())) return false;
    // Reject "Mrs. Smith"-style tokens where the first whitespace-separated word
    // is a title stopword. Without this, "Mr. and Mrs. Smith and Luna" would yield
    // ["Mrs. Smith", "Luna"].
    const firstWord = name.split(/\s+/)[0]?.toLowerCase();
    if (firstWord && MULTI_PATIENT_STOPWORDS.has(firstWord)) return false;
    return true;
};

export function checkForMultiplePatientNames(patientName: string): MultiPatientData[] {
    if (!patientName || !patientName.trim()) return [];

    const splitOnConnector = patientName.split(/\s(?:&|and)\s/i);
    if (splitOnConnector.length < 2) return [];

    // Also split each chunk on commas so "Bella, Max and Luna" and "Bella and Max, Luna" both yield 3 names.
    // Trailing punctuation (e.g. "Bella." from "Bella. and Max") is stripped so it doesn't leak into state.
    const cleaned = splitOnConnector
        .flatMap((chunk) => chunk.split(","))
        .map((name) => (name?.trim() || "").replace(TRAILING_PUNCT, ""))
        .filter(isLikelyPatientName);
    if (cleaned.length < 2) return [];

    return cleaned.map((name, index) => ({ name, isSelected: true, index }));
}

export default activePatientDataSlice.reducer;
